import DataService from "./dataservice.js";

let priceFilter = [];
let colorFilter = [];
let ratingFilter = [];
let prodTimeFilter = [];
let imprintLocationCountFilter = [];
let imprintColorCountFilter = [];
let tagFilter = [];
let sortFilter = '';
let pageNumber = 0;
let pageSize = 0;
let subSearchFilter = '';
let productQuantityFilter = '';
const colorId = 'chkColor_';
const ratingId = 'chkRating_';
const tagId = 'chkTag_';
const prodTimeId = 'chkProductionTime_';
const imprintLocationCountId = 'chkImprintLocationCount_';
const imprintColorCountId = 'chkImprintColorCount_';
const sortId = 'chksort_';
const defaultSort = '-RelevenceScore';
const defaultPageSize = 50;

// Maps each filter-id prefix to its backing array.
// filter is null for scalar types (sort). To add a new filter type, add one
// entry here — addFilter, removeFilter, getSelectedFilters, and getFilterId
// all update automatically.
const filterTypes = [
    { prefix: colorId,                filter: colorFilter                },
    { prefix: ratingId,               filter: ratingFilter               },
    { prefix: prodTimeId,             filter: prodTimeFilter             },
    { prefix: imprintLocationCountId, filter: imprintLocationCountFilter },
    { prefix: imprintColorCountId,    filter: imprintColorCountFilter    },
    { prefix: tagId,                  filter: tagFilter                  },
    { prefix: sortId,                 filter: null                       },
];

class FilterValues {
    /**
     * Adds a filter value to the appropriate filter array and logs the filter change.
     * @param {string} filterId 
     * @param {boolean} logFilter 
     * @returns {void}
     */
    addFilter(filterId, logFilter = true) {
        const filterType = resolveFilterType(filterId);
        if (!filterType) return;

        const filterValue = filterId.replace(filterType.prefix, '');
        if (filterType.filter) {
            filterType.filter.push(filterValue);
            if (logFilter) DataService.filterLog(filterId, filterValue);
        } else {
            sortFilter = filterValue;
            if (logFilter) DataService.filterLog('sortDropDown', filterValue);
        }
    };

    /**
     * Adds a price range filter and logs the filter change.
     * @param {string} filterId 
     * @param {number} priceRangeMin 
     * @param {number} priceRangeMax 
     * @param {boolean} logFilter 
     * @returns {void}
     */
    addPriceRangeFilter(filterId, priceRangeMin, priceRangeMax, logFilter) {
        if (typeof (logFilter) === 'undefined') logFilter = true;
        while (priceFilter.length) {
            priceFilter.shift();
        }

        priceFilter.push(priceRangeMin);
        priceFilter.push(priceRangeMax);
        let filterValue = priceRangeMin + '-' + priceRangeMax;
        if (logFilter === true) {
            DataService.filterLog(filterId, filterValue);
        }
    };

    /**
     * Adds a sub-search filter and logs the filter change.
     * @param {string} filterId 
     * @param {string} subSearch 
     * @param {boolean} logFilter 
     * @returns {void}
     */
    addSubSearchFilter(filterId, subSearch, logFilter) {
        if (typeof (logFilter) === 'undefined') logFilter = true;
        subSearchFilter = subSearch;
        if (logFilter === true) {
            DataService.filterLog(filterId, subSearch);
        }
    };

    /**
     * Adds a product quantity filter and logs the filter change.
     * @param {string} filterId 
     * @param {string} productQuantity 
     * @param {boolean} logFilter 
     * @returns {void}
     */
    addProductQuantityFilter(filterId, productQuantity, logFilter) {
        if (typeof (logFilter) === 'undefined') logFilter = true;
        productQuantityFilter = productQuantity;
        if (logFilter === true) {
            DataService.filterLog(filterId, productQuantity);
        }
    };

    /**
     * Gets the selected filters for a given filterId.
     * @param {string} filterId 
     * @returns {Array} filter or []
     */
    getSelectedFilters(filterId) {
        return resolveFilterType(filterId)?.filter ?? [];
    };

    /**
     * Gets the filter value for a given filterId.
     * @param {string} filterId 
     * @returns {string|undefined} filter value or undefined
     */
    getFilterId(filterId) {
        const filterType = resolveFilterType(filterId);
        return filterType ? filterId.replace(filterType.prefix, '') : undefined;
    };

    /**
     * Adds a page number filter.
     * @param {number} pageNo 
     * @returns {void}
     */
    addPageNumber(pageNo) {
        pageNumber = pageNo;
    };

    /**
     * Adds a page size filter.
     * @param {number} size 
     * @returns {void}
     */
    addPageSize(size) {
        pageSize = size;
    };

    /**
     * Removes a filter for a given filterId.
     * @param {string} filterId 
     * @returns {void}
     */
    removeFilter(filterId) {
        const filterType = resolveFilterType(filterId);
        if (!filterType) return;

        if (!filterType.filter) {
            sortFilter = defaultSort;
            return;
        }

        const filterValue = filterId.replace(filterType.prefix, '');
        const index = filterType.filter.indexOf(filterValue);
        if (index > -1) filterType.filter.splice(index, 1);
    };

    /**
     * Removes the price range filter.
     * @returns {void}
     */
    removePriceRangeFilter() {
        while (priceFilter.length) {
            priceFilter.shift();
        }
    };

    /**
     * Removes the sub-search filter.
     * @returns {void}
     */
    removeSubSearchFilter() {
        subSearchFilter = '';
    };

    /**
     * Removes the product quantity filter.
     * @returns {void}
     */
    removeProductQuantityFilter() {
        productQuantityFilter = '';
    };

    /**
     * Clears all filters.
     * @returns {void}
     */
    clearFilters() {
        pageNumber = 0;
        subSearchFilter = '';
        productQuantityFilter = '';

        while (priceFilter.length) {
            priceFilter.shift();
        }
        while (colorFilter.length) {
            colorFilter.shift();
        }
        while (ratingFilter.length) {
            ratingFilter.shift();
        }
        while (prodTimeFilter.length) {
            prodTimeFilter.shift();
        }
        while (imprintLocationCountFilter.length) {
            imprintLocationCountFilter.shift();
        }
        while (imprintColorCountFilter.length) {
            imprintColorCountFilter.shift();
        }
        while (tagFilter.length) {
            tagFilter.shift();
        }
    };

    /**
     * Gets the colorId for a given color.
     * @param {string} color 
     * @returns {string} colorId
     */
    getColorElementId(color) {
        return colorId + color;
    };

    /**
     * Gets the ratingId for a given rating.
     * @param {string} rating 
     * @returns {string} ratingId
     */
    getRatingElementId(rating) {
        return ratingId + rating;
    };

    /**
     * Gets the prodTimeId for a given production time.
     * @param {string} time 
     * @returns {string} prodTimeId
     */
    getProductionTimeElementId(time) {
        return prodTimeId + time;
    };

    /**
     * Gets the imprintLocationCountId for a given imprint location count.
     * @param {string} imprintLocationCount 
     * @returns {string} imprintLocationCountId
     */
    getImprintLocationCountElementId(imprintLocationCount) {
        return imprintLocationCountId + imprintLocationCount;
    };

    /**
     * Gets the imprintColorCountId for a given imprint color count.
     * @param {string} imprintColorCount 
     * @returns {string} imprintColorCountId
     */
    getImprintColorCountElementId(imprintColorCount) {
        return imprintColorCountId + imprintColorCount;
    };

    /**
     * Gets the tagId for a given tag.
     * @param {string} tag 
     * @returns {string} tagId
     */
    getTagElementId(tag) {
        return tagId + tag;
    };

    /**
     * Gets the sortId for a given sort.
     * @param {string} sort 
     * @returns {string} sortId
     */
    getSortElementId(sort) {
        return sortId + sort;
    };

    /**
     * Gets the price filter.
     * @returns {Array} priceFilter
     */
    getPriceFilter() {
        return priceFilter;
    };

    /**
     * Gets the color filter.
     * @returns {Array} colorFilter
     */
    getColorFilter() {
        return colorFilter;
    };

    /**
     * Gets the rating filter.
     * @returns {Array} ratingFilter
     */
    getRatingFilter() {
        return ratingFilter;
    };

    /**
     * Gets the production time filter.
     * @returns {Array} prodTimeFilter
     */
    getProdTimeFilter() {
        return prodTimeFilter;
    };

    /**
     * Gets the imprint location count filter.
     * @returns {Array} imprintLocationCountFilter
     */
    getImprintLocationCountFilter() {
        return imprintLocationCountFilter;
    };

    /**
     * Gets the imprint color count filter.
     * @returns {Array} imprintColorCountFilter
     */
    getImprintColorCountFilter() {
        return imprintColorCountFilter;
    };

    /**
     * Gets the tag filter.
     * @returns {Array} tagFilter
     */
    getTagFilter() {
        return tagFilter;
    };

    /**
     * Gets the sort filter.
     * @returns {Array} sortFilter
     */
    getSortFilter() {
        return sortFilter;
    };

    /**
     * Gets the current page number.
     * @returns {number} pageNumber
     */
    getPageNumber() {
        return pageNumber;
    };

    /**
     * Gets the current page size.
     * @returns {number} pageSize
     */
    getPageSize() {
        return pageSize;
    };

    /**
     * Gets the default sort.
     * @returns {string} defaultSort
     */
    getDefaultSort() {
        return defaultSort;
    };

    /**
     * Gets the default page size.
     * @returns {number} defaultPageSize
     */
    getDefaultPageSize() {
        return defaultPageSize;
    };

    /**
     * Gets the sub search filter.
     * @returns {Array} subSearchFilter
     */
    getSubSearchFilter() {
        return subSearchFilter;
    };

    /**
     * Gets the product quantity filter.
     * @returns {Array} productQuantityFilter
     */
    getProductQuantityFilter() {
        return productQuantityFilter;
    };

    /**
     * Checks if the given filterId is a color filter.
     * @param {string} filterId 
     * @returns {boolean}
     */
    isColorId(filterId) {
        return filterId.startsWith(colorId);
    };

    /**
     * Checks if the given filterId is a rating filter.
     * @param {string} filterId 
     * @returns {boolean}
     */
    isRatingId(filterId) {
        return filterId.startsWith(ratingId);
    };
}

function resolveFilterType(filterId) {
    return filterTypes.find(t => filterId.startsWith(t.prefix)) ?? null;
};

export default new FilterValues();